import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../config/URL";
import fetchAllCentersWithIds from "../../pages/List/CenterList";
import Logo from "../../assets/images/Logo-Portal_Access.png";
import CustomDatePicker from "../../pages/CustomDatePicker";
import PhoneInput from "react-phone-input-2";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdContentCopy } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";

// Utility function to format date to yyyy-mm-dd
const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Phone number validation helper
const isValidPhone = (value) => {
  if (!value) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

// Yup validation schema
const buildValidationSchema = (termsList) =>
  Yup.object({
    centerId: Yup.string().required("Centre is required"),
    studentName: Yup.string().trim().required("Student name is required"),
    gender: Yup.string().required("Gender is required"),
    dateOfBirth: Yup.date()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable()
      .required("Date of birth is required")
      .max(new Date(), "Date of birth cannot be in the future"),
    medicalCondition: Yup.string()
      .trim()
      .required("Medical condition is required"),
    schoolType: Yup.string().required("School type is required"),
    ethnicGroup: Yup.string().required("Ethnic group is required"),
    schoolName: Yup.string().trim().required("School name is required"),
    allowSocialMedia: Yup.string().required("Allow Social Media is required"),
    allowMagazine: Yup.string().required("Allow Magazine is required"),
    primaryContact: Yup.string().required("Primary contact is required"),

    father: Yup.object({
      fatherFullName: Yup.string()
        .trim()
        .required("Father's full name is required"),
      fatherOccupation: Yup.string()
        .trim()
        .required("Father's occupation is required"),
      fatherDateOfBirth: Yup.date()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .nullable()
        .required("Father's date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future"),
      fatherMobileNumber: Yup.string()
        .required("Father's mobile number is required")
        .test(
          "valid-phone",
          "Please enter a valid mobile number (10–15 digits)",
          (value) => isValidPhone(value),
        ),
      fatherEmail: Yup.string()
        .email("Invalid email address")
        .required("Father's email is required"),
      fatherAddress: Yup.string()
        .trim()
        .required("Father's address is required"),
      fatherPostalCode: Yup.string()
        .matches(/^\d{6}$/, "Postal code must be exactly 6 digits")
        .required("Father's postal code is required"),
    }),

    mother: Yup.object({
      motherFullName: Yup.string()
        .trim()
        .required("Mother's full name is required"),
      motherOccupation: Yup.string()
        .trim()
        .required("Mother's occupation is required"),
      motherDateOfBirth: Yup.date()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .nullable()
        .required("Mother's date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future"),
      motherMobileNumber: Yup.string()
        .required("Mother's mobile number is required")
        .test(
          "valid-phone",
          "Please enter a valid mobile number (10–15 digits)",
          (value) => isValidPhone(value),
        ),
      motherEmail: Yup.string()
        .email("Invalid email address")
        .required("Mother's email is required"),
      motherAddress: Yup.string()
        .trim()
        .required("Mother's address is required"),
      motherPostalCode: Yup.string()
        .matches(/^\d{6}$/, "Postal code must be exactly 6 digits")
        .required("Mother's postal code is required"),
    }),

    terms: Yup.object()
      .required("You must accept all terms")
      .test(
        "all-terms-accepted",
        "You must accept all terms",
        function (value) {
          if (!value || !termsList.length) return false;
          return termsList.every((item) => value[item.id] === true);
        },
      ),
  });

// Initial form values factory
const getInitialValues = () => ({
  centerId: "",
  studentName: "",
  gender: "",
  dateOfBirth: "",
  medicalCondition: "",
  schoolType: "",
  ethnicGroup: "",
  schoolName: "",
  primaryContact: "",
  allowMagazine: "",
  allowSocialMedia: "",
  father: {
    fatherFullName: "",
    fatherOccupation: "",
    fatherDateOfBirth: "",
    fatherMobileNumber: "",
    fatherEmail: "",
    fatherAddress: "",
    fatherPostalCode: "",
    fatherBoolean: true,
  },
  mother: {
    motherFullName: "",
    motherOccupation: "",
    motherDateOfBirth: "",
    motherMobileNumber: "",
    motherEmail: "",
    motherAddress: "",
    motherPostalCode: "",
    motherBoolean: false,
  },
  emergencyContact: {
    emergencyContactName: "",
    emergencyContactNumber: "",
    emergencyContactRelationship: "",
  },
  authorizedContact: {
    authorizedContactName: "",
    authorizedContactNumber: "",
    authorizedContactRelationship: "",
    authorizedAddress: "",
  },
  terms: {},
});

function NewStudentForm() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [centerData, setCenterData] = useState([]);
  const [ethnicGroup, setEthnicGroup] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const redirectUrl = location.state?.redirectUrl || null;

  // Fetch terms and conditions
  const getData = async () => {
    try {
      const res = await api.get(`/getAllDescriptionPublish`);
      setTermsList(res.data || []);
    } catch (error) {
      toast.error("Error fetching terms and conditions");
    }
  };

  // Fetch center data
  const fetchCenterData = async () => {
    try {
      const response = await fetchAllCentersWithIds();
      if (Array.isArray(response)) {
        setCenterData(response);
      } else {
        setCenterData([]);
      }
    } catch (error) {
      toast.error("Failed to fetch center data");
    }
  };

  // Fetch ethnic group data
  const fetchEthnicGroupData = async () => {
    try {
      const response = await api.get(`/getAllRaceWithOutToken`);
      setEthnicGroup(response.data);
    } catch (error) {
      toast.error("Failed to fetch ethnic group data");
    }
  };

  useEffect(() => {
    fetchCenterData();
    fetchEthnicGroupData();
    getData();
  }, []);

  // Sync terms initial values once termsList loads
  useEffect(() => {
    if (termsList.length > 0) {
      const initialTerms = {};
      termsList.forEach((item) => {
        initialTerms[item.id] = false;
      });
      formik.setFieldValue("terms", initialTerms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsList]);

  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema: buildValidationSchema(termsList),
    onSubmit: async (values) => {
      setLoadIndicator(true);

      // Build termsAndConditionX payload
      const termsPayload = {};
      termsList.forEach((item, index) => {
        termsPayload[`termsAndCondition${index + 1}`] =
          values.terms?.[item.id] || false;
      });

      const formattedValues = {
        ...values,
        terms: undefined,
        ...termsPayload,
        dateOfBirth: formatDate(values.dateOfBirth),
        father: {
          ...values.father,
          fatherDateOfBirth: formatDate(values.father.fatherDateOfBirth),
          fatherBoolean: values.primaryContact === "Father",
        },
        mother: {
          ...values.mother,
          motherDateOfBirth: formatDate(values.mother.motherDateOfBirth),
          motherBoolean: values.primaryContact === "Mother",
        },
      };

      try {
        const response = await api.post(
          "/studentEnrollmentForm",
          formattedValues,
          {
            headers: { "Content-Type": "application/json" },
          },
        );

        if (response.status === 201 || response.status === 200) {
          toast.success(response.data.message || "Form submitted successfully");
          formik.resetForm({ values: getInitialValues() });
          // Re-init terms after reset
          if (termsList.length > 0) {
            const initialTerms = {};
            termsList.forEach((item) => {
              initialTerms[item.id] = false;
            });
            formik.setFieldValue("terms", initialTerms);
          }
          if (redirectUrl) navigate(redirectUrl);
        } else {
          toast.error(response.data.message || "Failed to submit form");
        }
      } catch (error) {
        if (
          error?.response?.status === 409 ||
          error?.response?.status === 404
        ) {
          toast.warning(
            error?.response?.data?.message || "Conflict or not found",
          );
        } else {
          toast.error("Error submitting data: " + error.message);
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  // Scroll to first error on submit attempt
  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      // Flatten nested error keys for scrolling
      const findFirstErrorKey = (errors, prefix = "") => {
        for (const key of Object.keys(errors)) {
          const fullKey = prefix ? `${prefix}.${key}` : key;
          if (typeof errors[key] === "object" && errors[key] !== null) {
            const nested = findFirstErrorKey(errors[key], fullKey);
            if (nested) return nested;
          } else {
            return fullKey;
          }
        }
        return null;
      };

      const firstKey = findFirstErrorKey(formik.errors);
      if (firstKey) {
        const el =
          document.querySelector(`[name="${firstKey}"]`) ||
          document.querySelector(`[name="${firstKey.split(".").pop()}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus?.();
        }
      }
    }
  }, [formik.submitCount, formik.errors]);

  // Handle form clear
  const handleReset = () => {
    formik.resetForm({ values: getInitialValues() });
    toast.info("Form has been reset");
  };

  // Copy emergency contact → authorized contact
  const handleCopy = () => {
    formik.setFieldValue(
      "authorizedContact.authorizedContactName",
      formik.values.emergencyContact.emergencyContactName,
    );
    formik.setFieldValue(
      "authorizedContact.authorizedContactNumber",
      formik.values.emergencyContact.emergencyContactNumber,
    );
    formik.setFieldValue(
      "authorizedContact.authorizedContactRelationship",
      formik.values.emergencyContact.emergencyContactRelationship,
    );
  };

  // Helper: show error text
  const ErrorMsg = ({ msg }) =>
    msg ? (
      <div className="invalid-feedback d-block" style={{ fontSize: "13px" }}>
        {msg}
      </div>
    ) : null;

  return (
    <div className="container-fluid enrollment-bg">
      {/* ── Sticky Header ── */}
      <header
        className="p-2 bg-white shadow-sm"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <img
              src={Logo}
              alt="Logo"
              className="img-fluid"
              style={{ maxWidth: "70px", height: "auto" }}
            />
            <h4
              className="text-center flex-grow-1 m-0"
              style={{ color: "#287f71" }}
            >
              Enrollment Form
            </h4>
            <div />
          </div>
        </div>
      </header>

      {/* ── Main form — note the pb-5 so sticky footer doesn't cover content ── */}
      <div className="mt-5 pb-5">
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) e.preventDefault();
          }}
        >
          <div className="container">
            {/* ════════════════════════════════════
                STUDENT DETAILS
            ════════════════════════════════════ */}
            <div className="row py-4">
              <h6 className="mb-3 enrolled-h6">Student Details</h6>

              {/* Centre */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Centre<span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    formik.touched.centerId && formik.errors.centerId
                      ? "is-invalid"
                      : ""
                  }`}
                  name="centerId"
                  {...formik.getFieldProps("centerId")}
                >
                  <option value="">Select Centre</option>
                  {centerData.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.centerNames}
                    </option>
                  ))}
                </select>
                <ErrorMsg
                  msg={formik.touched.centerId && formik.errors.centerId}
                />
              </div>

              {/* Student Name */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Student Name<span className="text-danger">*</span>
                </label>
                <input
                  name="studentName"
                  type="text"
                  className={`form-control ${
                    formik.touched.studentName && formik.errors.studentName
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("studentName")}
                />
                <ErrorMsg
                  msg={formik.touched.studentName && formik.errors.studentName}
                />
              </div>

              {/* Date of Birth */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Date of Birth<span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  name="dateOfBirth"
                  value={formik.values.dateOfBirth}
                  onChange={(date) => formik.setFieldValue("dateOfBirth", date)}
                  onBlur={formik.handleBlur}
                  error={formik.errors.dateOfBirth}
                  touched={formik.touched.dateOfBirth}
                  showPopperArrow={true}
                />
                <ErrorMsg
                  msg={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                />
              </div>

              {/* Gender */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Gender<span className="text-danger">*</span>
                </label>
                <div>
                  {["Male", "Female"].map((g) => (
                    <div key={g} className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="gender"
                        value={g}
                        checked={formik.values.gender === g}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <label className="form-check-label">{g}</label>
                    </div>
                  ))}
                  <ErrorMsg
                    msg={formik.touched.gender && formik.errors.gender}
                  />
                </div>
              </div>

              {/* Medical Condition */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Medical Condition<span className="text-danger">*</span>
                </label>
                <input
                  name="medicalCondition"
                  type="text"
                  className={`form-control ${
                    formik.touched.medicalCondition &&
                    formik.errors.medicalCondition
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("medicalCondition")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.medicalCondition &&
                    formik.errors.medicalCondition
                  }
                />
              </div>

              {/* Ethnic Group */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Ethnic Group<span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    formik.touched.ethnicGroup && formik.errors.ethnicGroup
                      ? "is-invalid"
                      : ""
                  }`}
                  name="ethnicGroup"
                  {...formik.getFieldProps("ethnicGroup")}
                >
                  <option value="">Select Ethnic Group</option>
                  {ethnicGroup.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.race}
                    </option>
                  ))}
                </select>
                <ErrorMsg
                  msg={formik.touched.ethnicGroup && formik.errors.ethnicGroup}
                />
              </div>

              {/* School Name */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  School Name<span className="text-danger">*</span>
                </label>
                <input
                  name="schoolName"
                  type="text"
                  className={`form-control ${
                    formik.touched.schoolName && formik.errors.schoolName
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("schoolName")}
                />
                <ErrorMsg
                  msg={formik.touched.schoolName && formik.errors.schoolName}
                />
              </div>

              {/* School Type */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  School Type<span className="text-danger">*</span>
                </label>
                <div>
                  {[
                    { value: "CHILDCARE", label: "Childcare" },
                    { value: "KINDERGARTEN", label: "Kindergarten" },
                    { value: "OTHERS", label: "Others" },
                  ].map((st) => (
                    <div
                      key={st.value}
                      className="form-check form-check-inline"
                    >
                      <input
                        className="form-check-input"
                        type="radio"
                        name="schoolType"
                        value={st.value}
                        checked={formik.values.schoolType === st.value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <label className="form-check-label">{st.label}</label>
                    </div>
                  ))}
                  <ErrorMsg
                    msg={formik.touched.schoolType && formik.errors.schoolType}
                  />
                </div>
              </div>

              {/* Primary Contact */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Primary Contact<span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    formik.touched.primaryContact &&
                    formik.errors.primaryContact
                      ? "is-invalid"
                      : ""
                  }`}
                  name="primaryContact"
                  {...formik.getFieldProps("primaryContact")}
                >
                  <option value="">Select Primary Contact</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                </select>
                <ErrorMsg
                  msg={
                    formik.touched.primaryContact &&
                    formik.errors.primaryContact
                  }
                />
              </div>

              {/* Videography / Photography */}
              <div className="col-md-12 mb-3">
                <div className="row">
                  <h6 style={{ color: "#ff7500" }}>Videography/Photography</h6>

                  {/* Allow Magazine */}
                  <div className="col-lg-6 col-sm-12 mt-3 ps-4">
                    <label className="form-label">
                      Allow display in Facility Bulletin / Magazine / Advert
                      <span className="text-danger">*</span>
                    </label>
                    <div className="mt-2">
                      {["yes", "No"].map((v) => (
                        <React.Fragment key={v}>
                          <input
                            className="form-check-input mx-2"
                            type="radio"
                            name="allowMagazine"
                            value={v}
                            checked={formik.values.allowMagazine === v}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {v === "yes" ? "Yes" : "No"}&nbsp;&nbsp;&nbsp;
                        </React.Fragment>
                      ))}
                      <ErrorMsg
                        msg={
                          formik.touched.allowMagazine &&
                          formik.errors.allowMagazine
                        }
                      />
                    </div>
                  </div>

                  {/* Allow Social Media */}
                  <div className="col-lg-6 col-sm-12 mt-3">
                    <label className="form-label">
                      Allow display on Social Media
                      <span className="text-danger">*</span>
                    </label>
                    <div className="mt-2">
                      {["yes", "No"].map((v) => (
                        <React.Fragment key={v}>
                          <input
                            className="form-check-input mx-2"
                            type="radio"
                            name="allowSocialMedia"
                            value={v}
                            checked={formik.values.allowSocialMedia === v}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          {v === "yes" ? "Yes" : "No"}&nbsp;&nbsp;&nbsp;
                        </React.Fragment>
                      ))}
                      <ErrorMsg
                        msg={
                          formik.touched.allowSocialMedia &&
                          formik.errors.allowSocialMedia
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ════════════════════════════════════
                FATHER DETAILS
            ════════════════════════════════════ */}
            <div className="row py-4">
              <h6 className="mb-3 enrolled-h6">Father Details</h6>

              {/* Full Name */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Full Name<span className="text-danger">*</span>
                </label>
                <input
                  name="father.fatherFullName"
                  type="text"
                  className={`form-control ${
                    formik.touched.father?.fatherFullName &&
                    formik.errors.father?.fatherFullName
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("father.fatherFullName")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherFullName &&
                    formik.errors.father?.fatherFullName
                  }
                />
              </div>

              {/* Date of Birth */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Date of Birth<span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  name="father.fatherDateOfBirth"
                  value={formik.values.father.fatherDateOfBirth}
                  onChange={(date) =>
                    formik.setFieldValue("father.fatherDateOfBirth", date)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.errors.father?.fatherDateOfBirth}
                  touched={formik.touched.father?.fatherDateOfBirth}
                  showPopperArrow={true}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherDateOfBirth &&
                    formik.errors.father?.fatherDateOfBirth
                  }
                />
              </div>

              {/* Email */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Email<span className="text-danger">*</span>
                </label>
                <input
                  name="father.fatherEmail"
                  type="email"
                  className={`form-control ${
                    formik.touched.father?.fatherEmail &&
                    formik.errors.father?.fatherEmail
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("father.fatherEmail")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherEmail &&
                    formik.errors.father?.fatherEmail
                  }
                />
              </div>

              {/* Mobile Number */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Mobile Number<span className="text-danger">*</span>
                </label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "father.fatherMobileNumber",
                    required: true,
                    onBlur: formik.handleBlur,
                  }}
                  value={formik.values.father.fatherMobileNumber}
                  onChange={(phone) =>
                    formik.setFieldValue(
                      "father.fatherMobileNumber",
                      "+" + phone,
                    )
                  }
                  containerClass="phone-input-container"
                  inputClass={`phone-input ${
                    formik.touched.father?.fatherMobileNumber &&
                    formik.errors.father?.fatherMobileNumber
                      ? "is-invalid"
                      : ""
                  }`}
                  buttonClass="phone-flag-button"
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherMobileNumber &&
                    formik.errors.father?.fatherMobileNumber
                  }
                />
              </div>

              {/* Occupation */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Occupation<span className="text-danger">*</span>
                </label>
                <input
                  name="father.fatherOccupation"
                  type="text"
                  className={`form-control ${
                    formik.touched.father?.fatherOccupation &&
                    formik.errors.father?.fatherOccupation
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("father.fatherOccupation")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherOccupation &&
                    formik.errors.father?.fatherOccupation
                  }
                />
              </div>

              {/* Address */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Address<span className="text-danger">*</span>
                </label>
                <input
                  name="father.fatherAddress"
                  type="text"
                  className={`form-control ${
                    formik.touched.father?.fatherAddress &&
                    formik.errors.father?.fatherAddress
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("father.fatherAddress")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherAddress &&
                    formik.errors.father?.fatherAddress
                  }
                />
              </div>

              {/* Postal Code */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Postal Code<span className="text-danger">*</span>
                </label>
                <input
                  name="father.fatherPostalCode"
                  type="text"
                  maxLength={6}
                  className={`form-control ${
                    formik.touched.father?.fatherPostalCode &&
                    formik.errors.father?.fatherPostalCode
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("father.fatherPostalCode")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.father?.fatherPostalCode &&
                    formik.errors.father?.fatherPostalCode
                  }
                />
              </div>
            </div>

            {/* ════════════════════════════════════
                MOTHER DETAILS
            ════════════════════════════════════ */}
            <div className="row py-4">
              <h6 className="mb-3 enrolled-h6">Mother Details</h6>

              {/* Full Name */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Full Name<span className="text-danger">*</span>
                </label>
                <input
                  name="mother.motherFullName"
                  type="text"
                  className={`form-control ${
                    formik.touched.mother?.motherFullName &&
                    formik.errors.mother?.motherFullName
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("mother.motherFullName")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherFullName &&
                    formik.errors.mother?.motherFullName
                  }
                />
              </div>

              {/* Date of Birth */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Date of Birth<span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  name="mother.motherDateOfBirth"
                  value={formik.values.mother.motherDateOfBirth}
                  onChange={(date) =>
                    formik.setFieldValue("mother.motherDateOfBirth", date)
                  }
                  onBlur={formik.handleBlur}
                  error={formik.errors.mother?.motherDateOfBirth}
                  touched={formik.touched.mother?.motherDateOfBirth}
                  showPopperArrow={true}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherDateOfBirth &&
                    formik.errors.mother?.motherDateOfBirth
                  }
                />
              </div>

              {/* Email */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Email<span className="text-danger">*</span>
                </label>
                <input
                  name="mother.motherEmail"
                  type="email"
                  className={`form-control ${
                    formik.touched.mother?.motherEmail &&
                    formik.errors.mother?.motherEmail
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("mother.motherEmail")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherEmail &&
                    formik.errors.mother?.motherEmail
                  }
                />
              </div>

              {/* Mobile Number */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Mobile Number<span className="text-danger">*</span>
                </label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "mother.motherMobileNumber",
                    required: true,
                    onBlur: formik.handleBlur,
                  }}
                  value={formik.values.mother.motherMobileNumber}
                  onChange={(phone) =>
                    formik.setFieldValue(
                      "mother.motherMobileNumber",
                      "+" + phone,
                    )
                  }
                  containerClass="phone-input-container"
                  inputClass={`phone-input ${
                    formik.touched.mother?.motherMobileNumber &&
                    formik.errors.mother?.motherMobileNumber
                      ? "is-invalid"
                      : ""
                  }`}
                  buttonClass="phone-flag-button"
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherMobileNumber &&
                    formik.errors.mother?.motherMobileNumber
                  }
                />
              </div>

              {/* Occupation */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Occupation<span className="text-danger">*</span>
                </label>
                <input
                  name="mother.motherOccupation"
                  type="text"
                  className={`form-control ${
                    formik.touched.mother?.motherOccupation &&
                    formik.errors.mother?.motherOccupation
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("mother.motherOccupation")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherOccupation &&
                    formik.errors.mother?.motherOccupation
                  }
                />
              </div>

              {/* Address */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Address<span className="text-danger">*</span>
                </label>
                <input
                  name="mother.motherAddress"
                  type="text"
                  className={`form-control ${
                    formik.touched.mother?.motherAddress &&
                    formik.errors.mother?.motherAddress
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("mother.motherAddress")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherAddress &&
                    formik.errors.mother?.motherAddress
                  }
                />
              </div>

              {/* Postal Code */}
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Postal Code<span className="text-danger">*</span>
                </label>
                <input
                  name="mother.motherPostalCode"
                  type="text"
                  maxLength={6}
                  className={`form-control ${
                    formik.touched.mother?.motherPostalCode &&
                    formik.errors.mother?.motherPostalCode
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("mother.motherPostalCode")}
                />
                <ErrorMsg
                  msg={
                    formik.touched.mother?.motherPostalCode &&
                    formik.errors.mother?.motherPostalCode
                  }
                />
              </div>
            </div>

            {/* ════════════════════════════════════
                EMERGENCY CONTACT
            ════════════════════════════════════ */}
            <div className="row py-4">
              <h6 className="mb-3 enrolled-h6">
                Emergency Contact (Other than Parents)
              </h6>

              <div className="col-md-4 mb-3">
                <label className="form-label">Name</label>
                <input
                  name="emergencyContact.emergencyContactName"
                  type="text"
                  className="form-control"
                  {...formik.getFieldProps(
                    "emergencyContact.emergencyContactName",
                  )}
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Contact Number</label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "emergencyContact.emergencyContactNumber",
                    onBlur: formik.handleBlur,
                  }}
                  value={formik.values.emergencyContact.emergencyContactNumber}
                  onChange={(phone) =>
                    formik.setFieldValue(
                      "emergencyContact.emergencyContactNumber",
                      "+" + phone,
                    )
                  }
                  containerClass="phone-input-container"
                  inputClass="phone-input"
                  buttonClass="phone-flag-button"
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Relationship</label>
                <select
                  className="form-select"
                  name="emergencyContact.emergencyContactRelationship"
                  {...formik.getFieldProps(
                    "emergencyContact.emergencyContactRelationship",
                  )}
                >
                  <option value="">Select Relation</option>
                  {[
                    "Helper",
                    "Legal Guardian",
                    "Aunt",
                    "Uncle",
                    "Sibling",
                    "Grandfather",
                    "Grandmother",
                    "Others",
                  ].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ════════════════════════════════════
                AUTHORIZED CONTACT
            ════════════════════════════════════ */}
            <div className="row py-4">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-3 enrolled-h6">
                  Authorised Person to Take Child from Class (Other than
                  Parents)
                </h6>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="copy-tooltip">
                      Copy emergency contact details to authorized contact
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="btn btn-sm border-1 border-secondary ms-2"
                    onClick={handleCopy}
                  >
                    <MdContentCopy /> Same as Emergency Contact
                  </button>
                </OverlayTrigger>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Name</label>
                <input
                  name="authorizedContact.authorizedContactName"
                  type="text"
                  className="form-control"
                  {...formik.getFieldProps(
                    "authorizedContact.authorizedContactName",
                  )}
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Contact Number</label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "authorizedContact.authorizedContactNumber",
                    onBlur: formik.handleBlur,
                  }}
                  value={
                    formik.values.authorizedContact.authorizedContactNumber
                  }
                  onChange={(phone) =>
                    formik.setFieldValue(
                      "authorizedContact.authorizedContactNumber",
                      "+" + phone,
                    )
                  }
                  containerClass="phone-input-container"
                  inputClass="phone-input"
                  buttonClass="phone-flag-button"
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Relationship</label>
                <select
                  className="form-select"
                  name="authorizedContact.authorizedContactRelationship"
                  {...formik.getFieldProps(
                    "authorizedContact.authorizedContactRelationship",
                  )}
                >
                  <option value="">Select Relation</option>
                  {[
                    "Helper",
                    "Legal Guardian",
                    "Aunt",
                    "Uncle",
                    "Sibling",
                    "Grandfather",
                    "Grandmother",
                    "Others",
                  ].map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Address</label>
                <input
                  name="authorizedContact.authorizedAddress"
                  type="text"
                  className="form-control"
                  {...formik.getFieldProps(
                    "authorizedContact.authorizedAddress",
                  )}
                />
              </div>
            </div>

            {/* ════════════════════════════════════
                TERMS AND CONDITIONS
            ════════════════════════════════════ */}
            <div className="row py-2">
              {termsList.map((item) => (
                <div className="col-12 mt-3" key={item.id}>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id={`term-${item.id}`}
                      name={`terms.${item.id}`}
                      className={`form-check-input ${
                        formik.submitCount > 0 && formik.errors.terms
                          ? "is-invalid"
                          : ""
                      }`}
                      checked={formik.values.terms?.[item.id] || false}
                      onChange={() =>
                        formik.setFieldValue(
                          `terms.${item.id}`,
                          !formik.values.terms?.[item.id],
                        )
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`term-${item.id}`}
                    >
                      {item.description}
                      <span className="text-danger">*</span>
                    </label>
                  </div>
                </div>
              ))}

              {/* Show single terms error once after all checkboxes */}
              {formik.submitCount > 0 &&
                typeof formik.errors.terms === "string" && (
                  <div className="col-12 mt-2">
                    <small className="text-danger">{formik.errors.terms}</small>
                  </div>
                )}
            </div>
          </div>

          {/* ── Links row ── */}
          <div className="d-flex justify-content-center align-items-center gap-3 mt-4 mb-5">
            <a
              href="https://artylearning.com/terms&conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              Terms and Conditions
            </a>
            <span>&amp;</span>
            <a
              href="https://artylearning.com/privacypolicy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none"
            >
              Privacy Policy
            </a>
          </div>

          {/* ════════════════════════════════════
              STICKY FOOTER — buttons are INSIDE <form>
              so type="submit" triggers formik.handleSubmit
          ════════════════════════════════════ */}
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              borderTop: "1px solid #dee2e6",
              padding: "10px 20px",
              zIndex: 1000,
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button
              type="button"
              className="btn btn-sm btn-border border-1"
              onClick={handleReset}
              disabled={loadIndicator}
            >
              <span className="fw-medium">Clear</span>
            </button>

            {/* ✅ type="submit" inside <form> — triggers validation + API call */}
            <button
              type="submit"
              className="btn btn-sm btn-button"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
              )}
              <span className="fw-medium">
                {loadIndicator ? "Submitting..." : "Submit"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewStudentForm;
